<template>
    <v-card-text>
        <v-card>
            <v-card-title class="text-h5" primary-title>
                <v-row>
                    <v-col>
                        Factors
                    </v-col>
                    <v-spacer />
                    <v-col class="text-right" v-if="isDraft">
                        <v-btn @click="showAddGlobalRegistryFactorTypeDialog" color="primary">Add Global Registry Factor Type</v-btn>
                        &nbsp;
                        <v-btn @click="showAddAdHocFactorTypeDialog" color="primary">Add Ad Hoc Factor Type</v-btn>
                    </v-col>
                </v-row>
            </v-card-title>

            <!-- Application factors -->
            <v-card-text>
                <v-card>
                    <v-card-title>
                        Application factors
                    </v-card-title>
                    <v-card-text>
                        <v-row>
                            <v-col>
                                <v-checkbox v-model="selectedApplicationFactorOptions"
                                            v-for="applicationFactorOption of availableApplicationFactorOptions"
                                            :disabled="!isDraft"
                                            :key="applicationFactorOption.application.id"
                                            :label="applicationFactorOption.application.thingInstanceDataId"
                                            title="thingInstanceDataId"
                                            :value="applicationFactorOption"
                                            density="compact"
                                            @update:model-value="handleApplicationFactorChange"
                                >
                                </v-checkbox>
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>
            </v-card-text>

            <!-- Ad hoc factors -->
            <v-card-text v-for="adHocFactorTypeDTOItem in adHocFactorTypeDTOItems">
                <v-card>
                    <v-card-title>
                        {{ adHocFactorTypeDTOItem.name }} factors
                        <v-icon v-if="adHocFactorTypeDTOItem.adHocFactors.length === 0"
                                @click="deleteAdHocFactorType(adHocFactorTypeDTOItem)"
                                color="error">$trash-can-outline</v-icon>
                    </v-card-title>
                    <v-card-text>
                        <v-row>
                            <v-col cols="3">
                                <v-text-field :label="`Add ${ adHocFactorTypeDTOItem.name } factor`"
                                              v-model="adHocFactorTypeDTOItem.newAdHocFactorValue"
                                              @change="handleAddAdHocFactor(adHocFactorTypeDTOItem, $event.target.value)"
                                />
                            </v-col>
                            <v-col v-if="adHocFactorTypeDTOItem.adHocFactors.length === 0">
                                No {{ adHocFactorTypeDTOItem.name }} factors
                            </v-col>
                            <v-col>
                                <v-chip-group>
                                    <v-chip v-for="adHocFactor of adHocFactorTypeDTOItem.adHocFactors"
                                            :key="adHocFactor.id">
                                        <template v-slot:append>
                                            <v-icon class="ml-2" @click="editAdHocFactor(adHocFactor)">$pencil</v-icon>
                                            <v-icon class="ml-2" @click="removeAdHocFactor(adHocFactor)">$trash-can-outline</v-icon>
                                        </template>
                                        {{ adHocFactor.value }}
                                    </v-chip>
                                </v-chip-group>
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>
            </v-card-text>

            <!-- Global registry factors -->
            <v-card-text v-for="globalRegistryFactorTypeDTOItem in globalRegistryFactorTypeDTOItems">
                <v-card>
                    <v-card-title>
                        {{ globalRegistryFactorTypeDTOItem.globalRegistry.displayName }} factors
                        <v-icon v-if="globalRegistryFactorTypeDTOItem.globalRegistryFactors.length === 0"
                                @click="deleteGlobalRegistryFactorType(globalRegistryFactorTypeDTOItem)"
                                :disabled="!isDraft"
                                color="error">$trash-can-outline</v-icon>
                    </v-card-title>
                    <v-card-text>
                        <v-row>
                            <v-col cols="3">
                                <v-autocomplete :label="`Add ${ globalRegistryFactorTypeDTOItem.globalRegistry.displayName } factor`"
                                                :items="globalRegistryFactorTypeDTOItem.globalRegistry.registryEntries"
                                                :disabled="!isDraft"
                                                item-title="displayName"
                                                item-value="id"
                                                v-model="globalRegistryFactorTypeDTOItem.newGlobalRegistryFactorEntryId"
                                />
                            </v-col>
                            <v-col cols="1">
                                <v-btn @click="addGlobalRegistryEntryFactor(globalRegistryFactorTypeDTOItem)"
                                       color="primary">Add</v-btn>
                            </v-col>
                            <v-col v-if="globalRegistryFactorTypeDTOItem.globalRegistryFactors.length === 0">
                                No {{ globalRegistryFactorTypeDTOItem.globalRegistry.name }} factors
                            </v-col>
                            <v-col>
                                <v-chip-group>
                                    <v-chip v-for="globalRegistryFactor of globalRegistryFactorTypeDTOItem.globalRegistryFactors"
                                            :key="globalRegistryFactor.id">
                                        <template v-slot:append>
                                            <v-icon class="ml-2" @click="removeGlobalRegistryFactor(globalRegistryFactor)">$trash-can-outline</v-icon>
                                        </template>
                                        {{ getGlobalRegistryDisplayName(globalRegistryFactor) }}
                                    </v-chip>
                                </v-chip-group>
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>
            </v-card-text>

            <v-card-text v-if="isDraft">
                <v-card style="width: auto !important;">
                    <v-card-title>Treatment Factors</v-card-title>
                    <v-card-subtitle>
                        Configure which factors apply to which treatments
                    </v-card-subtitle>
                    <v-card-text>
                        <v-table density="compact" hover striped class="treatments-table">
                            <thead>
                            <tr>
                                <td>
                                    Treatment
                                </td>
                                <td v-for="applicationFactor of applicationFactors">
                                    {{ applicationFactor.thingInstanceDataId }}
                                </td>
                                <td v-for="adHocFactor of allAdHocFactors">
                                    {{ adHocFactor.value }}
                                </td>
                                <td v-for="globalRegistryFactor of allGlobalRegistryFactors">
                                    {{ getGlobalRegistryDisplayName(globalRegistryFactor) }}
                                </td>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="treatment of treatments">
                                <td>
                                    {{ treatment.name }}
                                </td>
                                <td v-for="applicationFactor of applicationFactors">
                                    <v-checkbox v-model="factorSelectionsByTreatmentId[treatment.id].applicationFactorIdSelections[applicationFactor.id]"
                                                @update:modelValue="updateConditionsFromFactorSelections"
                                                :disabled="!isDraft"
                                    />
                                </td>
                                <td v-for="adHocFactor of allAdHocFactors">
                                    <v-checkbox v-model="factorSelectionsByTreatmentId[treatment.id].factorIdSelectionsByAdHocFactorType[adHocFactor.adHocFactorTypeId].factorIdSelections[adHocFactor.id]"
                                                @update:modelValue="updateConditionsFromFactorSelections"
                                                :disabled="!isDraft"
                                    />
                                </td>
                                <td v-for="globalRegistryFactor of allGlobalRegistryFactors">
                                    <v-checkbox v-model="factorSelectionsByTreatmentId[treatment.id].factorIdSelectionsByGlobalRegistryFactorType[globalRegistryFactor.globalRegistryFactorTypeId].factorIdSelections[globalRegistryFactor.id]"
                                                @update:modelValue="updateConditionsFromFactorSelections"
                                                :disabled="!isDraft"
                                    />
                                </td>
                            </tr>
                            </tbody>
                        </v-table>
                    </v-card-text>
                </v-card>
            </v-card-text>

            <!-- condition templates from treatment/factor selections table -->
            <v-card-text>
                <v-card>
                    <v-card-title>Conditions</v-card-title>
                    <v-card-text>
                        <v-data-table :items="conditions"
                                      :headers="upsertConditionTableHeaders">
                            <template v-slot:item.name="{ item }">
                                <!-- XXX -->
                                <div v-if="isShowEditConditionName(item)" class="d-flex align-center">
                                    <v-text-field v-model="item.name">
                                        <template v-slot:append v-if="!isDraft">
                                            <v-btn @click="updateConditionName(item)"
                                                   color="primary"
                                                   class="mx-4">Save</v-btn>
                                            <v-btn @click="cancelEditConditionName()"
                                                   color="primary">Cancel</v-btn>
                                        </template>
                                    </v-text-field>
                                </div>
                                <div v-if="!isShowEditConditionName(item)">
                                    <span>{{ item.name }}</span>
                                    <v-icon-btn @click="editConditionName(item)"><v-icon>$pencil</v-icon></v-icon-btn>
                                </div>
                            </template>
                            <template v-slot:item.treatment="{ item }">
                                {{ item.treatment.name }}
                            </template>
                            <template v-slot:item.applicationFactors="{ item }">
                                {{ getApplicationFactors(item).join(", ") }}
                            </template>
                            <template  v-for="adHocFactorType in adHocFactorTypeDTOItems"
                                       v-slot:[`item.${adHocFactorType.name}`]="{ item }">
                                {{ getAdHocFactorValues(adHocFactorType, item).join(", ") }}
                            </template>
                            <template  v-for="globalRegistryFactorType in globalRegistryFactorTypeDTOItems"
                                       v-slot:[`item.${globalRegistryFactorType.globalRegistry.name}`]="{ item }">
                                {{ getGlobalRegistryFactorValues(globalRegistryFactorType, item).join(", ") }}
                            </template>
                        </v-data-table>
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn  @click="saveConditions"
                                :disabled="!isDraft"
                                color="primary">Save Conditions</v-btn>
                    </v-card-actions>
                </v-card>
            </v-card-text>
        </v-card>

        <v-dialog v-model="editAdHocFactorDialogVisible" width="50%">
            <v-card v-if="selectedAdHocFactor">
                <v-card-text>
                    <v-text-field v-model="selectedAdHocFactor.value" />
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn color="primary" @click="updateAdHocFactor">Update</v-btn>
                </v-card-actions>
            </v-card>
            Hello.
        </v-dialog>
        <v-dialog v-model="editAdHocFactorTypeDialogVisible" width="50%">
            <ad-hoc-factor-type-form
                :ad-hoc-factor-type="selectedAdHocFactorTypeDTO"
                @on-save="handleSaveAdHocType"
            />
        </v-dialog>
        <v-dialog v-model="editGlobalRegistryFactorTypeDialogVisible" width="50%">
            <global-registry-factor-type-form
                :global-registry-factor-type="selectedGlobalRegistryFactorTypeDTO"
                @on-save="handleSaveGlobalRegistryFactorType"
            />
        </v-dialog>
    </v-card-text>
</template>
<style>
.treatments-table table {
    border-collapse: collapse;
}

.treatments-table td {
    border: 1px solid #a9a9a9;
}

.treatments-table thead td {
    padding: 8px;
}
.no-wrap-checkbox {
    white-space: nowrap;
}

</style>
<script setup lang="ts">
import { withLoadingScreen } from "@/lib/loading";
import {
    AdHocFactorType,
    AdHocFactorTypeDTO,
    AllExperimentFactorsConfig,
    ApplicationFactorDTO,
    conditionFactorsAPI,
    GlobalRegistryFactorType,
    GlobalRegistryFactorTypeDTO,
    GlobalRegistryFactorTypeWithFactorsDTO,
    SavedAdHocFactor,
    SavedAdHocFactorType,
    SavedGlobalRegistryFactor,
    SavedGlobalRegistryFactorType,
} from "@/services/conditions/conditionFactors/conditionFactorsAPI";
import { computed, onMounted, ref } from "vue";
import { SavedApplication } from "@/services/applications/applicationsAPI";
import AdHocFactorTypeForm from "@/components/Conditions/AdHocFactorTypeForm.vue";
import GlobalRegistryFactorTypeForm from "@/components/Conditions/GlobalRegistryFactorTypeForm.vue";
import { Treatment } from "@/services/treatments/treatmentsAPI";
import {
    buildFactorSelectionsByTreatmentId,
    buildUpsertConditions,
    FactorSelections,
} from "@/services/conditions/conditionFactors/buildUpsertConditions";
import { VDataTableHeader } from "@/vuetifyTypeFixes";
import {
    conditionsAPI,
    mapUpsertConditionToDTO,
    UpsertCondition,
    UpsertConditionDTO,
} from "@/services/conditions/conditionsAPI";
import { ExperimentLike } from "@/services/experiments/experimentsAPI";
import { Condition } from "@/services/things/models";

type ApplicationFactorOption = {
    /**
     * ID of the SavedApplicationFactor
     */
    id: string | null;
    application: SavedApplication,
    selected: boolean;
}

type AdHocFactorTypeDTOItem = AdHocFactorTypeDTO & {
    newAdHocFactorValue: string;
}

type GlobalRegistryFactorTypeDTOItem = GlobalRegistryFactorTypeWithFactorsDTO & {
    newGlobalRegistryFactorEntryId: string;
}

const { experiment, treatments } = defineProps<{
    experiment: ExperimentLike;
    treatments: Treatment[];
}>();

const emit = defineEmits<{
    (e: "conditionsUpdated", conditions: Condition[]): void;
}>();

onMounted(async () => {
    await fetchConditions();
});

const applications = ref<SavedApplication[]>([]);
const applicationFactors = ref<ApplicationFactorDTO[]>([]);
const availableApplicationFactorOptions = ref<ApplicationFactorOption[]>([]);
const selectedApplicationFactorOptions = ref<ApplicationFactorOption[]>([]);
const adHocFactorTypeDTOItems = ref<AdHocFactorTypeDTOItem[]>([]);
const selectedAdHocFactor = ref<SavedAdHocFactor>();

const editApplicationFactorDialogVisible = ref(false);
const editAdHocFactorDialogVisible = ref(false);
const editAdHocFactorTypeDialogVisible = ref(false);
const selectedAdHocFactorTypeDTO = ref<AdHocFactorTypeDTO>();

const editGlobalRegistryFactorTypeDialogVisible = ref(false);
const selectedGlobalRegistryFactorTypeDTO = ref<GlobalRegistryFactorTypeDTO>();
const globalRegistryFactorTypeDTOItems = ref<GlobalRegistryFactorTypeDTOItem[]>([]);

const factorSelectionsByTreatmentId = ref<Record<string, FactorSelections>>({});
const upsertConditionTableHeaders = ref<VDataTableHeader[]>([]);
const conditions = ref<UpsertCondition[]>([]);

const isDraft = computed(() => {
    return experiment.setupStatus === "Draft";
})

function findApplicationById(id: string) {
    return applications.value.find((application) => application.id === id);
}

function getApplicationFactors(condition: UpsertCondition) {
    return condition.applicationFactors.map((factor) => {
        const application = findApplicationById(factor.applicationId);
        if (!application) {
            return `Unknown application ${factor.applicationId}`;
        }

        return application.thingInstanceDataId;
    });
}

function getAdHocFactorValues(adHocFactorType: SavedAdHocFactorType, condition: UpsertCondition) {
    const allAdHocFactors = condition.adHocFactors;
    const adHocFactorForType = allAdHocFactors.filter((adHocFactor) => adHocFactor.adHocFactorTypeId === adHocFactorType.id);
    return adHocFactorForType.map((factor) => factor.value);
}

function getGlobalRegistryFactorValues(globalRegistryFactorType: GlobalRegistryFactorTypeDTO, condition: UpsertCondition) {
    const allGlobalRegistryFactors = condition.globalRegistryFactors;
    const factorsForType = allGlobalRegistryFactors.filter((factor) => factor.globalRegistryFactorTypeId === globalRegistryFactorType.id);
    const entries = globalRegistryFactorType.globalRegistry.registryEntries;

    function findRegistryEntryById(id: string) {
        return entries.find((registryEntry) => registryEntry.id === id);
    }

    return factorsForType.map((factor) => {
        const registryEntry = findRegistryEntryById(factor.globalRegistryEntryId)
        if (registryEntry) {
            return registryEntry.displayName;
        } else {
            // TODO: warning that the entry isn't found?
            return factor.globalRegistryEntryId;
        }
    });
}

const allAdHocFactors = computed(() => {
    return adHocFactorTypeDTOItems.value.map((adHocFactorType) => {
        return adHocFactorType.adHocFactors;
    }).flat();
});

const allGlobalRegistryFactors = computed(() => {
    return globalRegistryFactorTypeDTOItems.value.map((globalRegistryFactorType) => {
        return globalRegistryFactorType.globalRegistryFactors;
    }).flat();
})

async function handleApplicationFactorChange() {
    const applications = selectedApplicationFactorOptions.value.map((option) => option.application);
    await withLoadingScreen(async () => {
        await conditionFactorsAPI.saveApplicationFactors({ experimentCode: experiment.code, applications });
        editApplicationFactorDialogVisible.value = false;
        await fetchConditions();
    });
}

async function showAddAdHocFactorTypeDialog() {
    editAdHocFactorTypeDialogVisible.value = true;
}

async function handleAddAdHocFactor(adHocFactorTypeDTO: AdHocFactorTypeDTO, value: string) {
    const trimmedValue = value.trim();
    if (trimmedValue.length === 0) {
        return;
    }

    const existing = adHocFactorTypeDTO.adHocFactors.find((adHocFactor) => adHocFactor.value === trimmedValue);
    if (existing) {
        return;
    }

    await withLoadingScreen(async () => {
        const result = await conditionFactorsAPI.createAdHocFactor({
            experimentCode: experiment.code,
            adHocFactor: {
                adHocFactorTypeId: adHocFactorTypeDTO.id,
                // TODO: provide a way to manually change the ordering, for now just increment and
                //  this doesn't support re-ordering after a delete.
                sortOrder: adHocFactorTypeDTO.adHocFactors.length + 1,
                value: trimmedValue
            }
        });

        handleFactorsResponse(result);
    })
}

async function removeAdHocFactor(adHocFactor: SavedAdHocFactor) {
    await withLoadingScreen(async () => {
        const result = await conditionFactorsAPI.deleteAdHocFactor({
            experimentCode: experiment.code,
            adHocFactor
        });

        handleFactorsResponse(result);
    })
}

async function updateAdHocFactor() {
    await withLoadingScreen(async () => {
        const result = await conditionFactorsAPI.updateAdHocFactor({
            experimentCode: experiment.code,
            adHocFactor: selectedAdHocFactor.value!
        });

        editAdHocFactorDialogVisible.value = false;

        handleFactorsResponse(result);
    })
}

async function editAdHocFactor(adHocFactor: SavedAdHocFactor) {
    selectedAdHocFactor.value = {
        ...adHocFactor
    };
    editAdHocFactorDialogVisible.value = true;
}

async function handleSaveAdHocType(adHocFactorType: AdHocFactorType | SavedAdHocFactorType) {
    await withLoadingScreen(async () => {
        const result = await conditionFactorsAPI.saveAdHocFactorType({ experimentCode: experiment.code, adHocFactorType });
        handleFactorsResponse(result);
    });
    editAdHocFactorTypeDialogVisible.value = false;
}

async function deleteAdHocFactorType(adHocFactorType: SavedAdHocFactorType) {
    await withLoadingScreen(async () => {
        const result = await conditionFactorsAPI.deleteAdHocFactorType({ experimentCode: experiment.code, adHocFactorType });
        handleFactorsResponse(result);
    });
}

function showAddGlobalRegistryFactorTypeDialog() {
    editGlobalRegistryFactorTypeDialogVisible.value = true;
}

async function handleSaveGlobalRegistryFactorType(globalRegistryFactorType: GlobalRegistryFactorType | SavedGlobalRegistryFactorType) {
    await withLoadingScreen(async () => {
        const result = await conditionFactorsAPI.createGlobalRegistryFactorType({ experimentCode: experiment.code, globalRegistryFactorType });
        handleFactorsResponse(result);
        editGlobalRegistryFactorTypeDialogVisible.value = false;
    });
}

async function deleteGlobalRegistryFactorType(globalRegistryFactorType: SavedGlobalRegistryFactorType) {
    await withLoadingScreen(async () => {
        const result = await conditionFactorsAPI.deleteGlobalRegistryFactorType({ experimentCode: experiment.code, globalRegistryFactorType });
        handleFactorsResponse(result);
    });
}

async function addGlobalRegistryEntryFactor(globalRegistryFactorTypeDTOItem: GlobalRegistryFactorTypeDTOItem) {
    const dupe = globalRegistryFactorTypeDTOItem.globalRegistryFactors.find((factor) => {
        return factor.globalRegistryEntryId === globalRegistryFactorTypeDTOItem.newGlobalRegistryFactorEntryId
    })

    if (dupe) {
        return;
    }

    await withLoadingScreen(async () => {
        const result = await conditionFactorsAPI.createGlobalRegistryFactor({
            experimentCode: experiment.code,
            globalRegistryFactor: {
                globalRegistryFactorTypeId: globalRegistryFactorTypeDTOItem.id,
                globalRegistryEntryId: globalRegistryFactorTypeDTOItem.newGlobalRegistryFactorEntryId,
            }

        });
        handleFactorsResponse(result);
    });
}

async function removeGlobalRegistryFactor(globalRegistryFactor: SavedGlobalRegistryFactor) {
    await withLoadingScreen(async () => {
        const result = await conditionFactorsAPI.deleteGlobalRegistryFactor({
            experimentCode: experiment.code,
            globalRegistryFactor
        });
        handleFactorsResponse(result);
    });
}

function getGlobalRegistryDisplayName(
    globalRegistryEntry: SavedGlobalRegistryFactor
) {
    const globalRegistryFactorTypeDTOItem = globalRegistryFactorTypeDTOItems.value.find((type) => {
        return type.id === globalRegistryEntry.globalRegistryFactorTypeId;
    });

    if (!globalRegistryFactorTypeDTOItem) {
        // Shouldn't happen
        throw new Error(`Could not find Global Registry Factor Type with id ${globalRegistryEntry.globalRegistryFactorTypeId}`);
    }

    const entry = globalRegistryFactorTypeDTOItem.globalRegistry.registryEntries.find(
        (entry) => entry.id === globalRegistryEntry.globalRegistryEntryId)

    if (entry) {
        return entry.displayName;
    } else {
        // shouldn't happen due to foreign keys
        return "unknown"
    }
}

async function fetchConditions() {
    await withLoadingScreen(async () => {
        const response = await conditionsAPI.fetchManageConditionsDTOForExperiment(experiment.code);
        conditions.value = response.conditions;
        emit("conditionsUpdated", conditions.value);
        handleFactorsResponse(response.allFactors);
    });
}

function handleFactorsResponse(allExperimentFactorsConfig: AllExperimentFactorsConfig) {
    applications.value = allExperimentFactorsConfig.applications;
    applicationFactors.value = allExperimentFactorsConfig.applicationFactors;
    adHocFactorTypeDTOItems.value = allExperimentFactorsConfig.adHocFactorTypes.map((adHocFactorTypeDTO) => {
        return {
            ...adHocFactorTypeDTO,
            newAdHocFactorValue: "",
        }
    })

    const availableApplicationOptions: ApplicationFactorOption[] = [];
    const selectedApplicationOptions: ApplicationFactorOption[] = [];
    for (const application of applications.value) {
        const option = buildOptionForApplication(application);
        availableApplicationOptions.push(option);
        if (option.selected) {
            selectedApplicationOptions.push(option);
        }
    }

    availableApplicationFactorOptions.value = availableApplicationOptions;
    selectedApplicationFactorOptions.value = selectedApplicationOptions;

    globalRegistryFactorTypeDTOItems.value = allExperimentFactorsConfig.globalRegistryFactorTypes.map((globalRegistryFactorTypeDTO) => {
        return {
            ...globalRegistryFactorTypeDTO,
            newGlobalRegistryFactorEntryId: "",
        }
    });

    upsertConditionTableHeaders.value = [
        {
            title: "Name",
            value: "name",
            align: "start",
            width: "20%",
            sortable: true,
            cellProps: { style: 'vertical-align: top;', class: 'pt-2' }
        },
        {
            title: "Treatment",
            value: "treatment",
            width: "10%",
            sortable: true,
        },
        {
            title: "Application",
            value: "applicationFactors",
            width: "10%",
            sortable: true,
        }
    ];

    for (const adHocFactorType of adHocFactorTypeDTOItems.value) {
        upsertConditionTableHeaders.value.push({
            title: `${adHocFactorType.name}`,
            value: `${adHocFactorType.name}`,
            align: "start",
            width: "5%",
        })
    }

    for (const globalRegistryFactorType of globalRegistryFactorTypeDTOItems.value) {
        upsertConditionTableHeaders.value.push({
            title: `${globalRegistryFactorType.globalRegistry.name}`,
            value: `${globalRegistryFactorType.globalRegistry.name}`,
            align: "start",
            width: "5%",
        })
    }

    if (isDraft.value) {
        // Only build up the conditions list from the factors if the experiment is in
        // draft. After the experiment is finalized, the conditions are stored with
        // foreign key references and can't be bulk modified from the factors.
        updateSelectedFactorIds();
        updateConditionsFromFactorSelections();
    }
}

function buildOptionForApplication(application: SavedApplication): ApplicationFactorOption {
    const applicationFactor = findApplicationFactorForApplicationId(application.id);
    const baseOption = {
        application,
    }

    if (applicationFactor) {
        return {
            ...baseOption,
            id: applicationFactor.id,
            selected: true
        }
    } else {
        return {
            ...baseOption,
            id: null,
            selected: false
        }
    }
}

function findApplicationFactorForApplicationId(applicationId: string): ApplicationFactorDTO | undefined {
    return applicationFactors.value.find((applicationFactor) => applicationFactor.applicationId === applicationId);
}

function updateSelectedFactorIds() {
    factorSelectionsByTreatmentId.value = buildFactorSelectionsByTreatmentId(
        conditions.value,
        treatments,
        applicationFactors.value,
        adHocFactorTypeDTOItems.value,
        globalRegistryFactorTypeDTOItems.value
    );
}

function updateConditionsFromFactorSelections() {
    const result = buildUpsertConditions(
        conditions.value,
        treatments,
        applicationFactors.value,
        adHocFactorTypeDTOItems.value,
        globalRegistryFactorTypeDTOItems.value,
        factorSelectionsByTreatmentId.value
    );

    conditions.value = result.upsertConditions;
}

async function saveConditions() {
    await withLoadingScreen(async () => {
        const upsertConditionDTOs: UpsertConditionDTO[] = conditions.value.map(mapUpsertConditionToDTO);
        conditions.value = await conditionsAPI.upsertConditionsDTOForExperiment(
            experiment.code,
            upsertConditionDTOs,
        );

        emit("conditionsUpdated", conditions.value);

        updateSelectedFactorIds();
    });
}

const conditionToEdit = ref<UpsertCondition>();

function isShowEditConditionName(condition: UpsertCondition) {
    return isDraft.value || (conditionToEdit.value && conditionToEdit.value.id === condition.id)
}

function editConditionName(condition: UpsertCondition) {
    conditionToEdit.value = condition;
}

async function updateConditionName(condition: UpsertCondition) {
    await withLoadingScreen(async () => {
        conditions.value = await conditionsAPI.updateCondition(experiment.code, {
            id: condition.id!,
            name: condition.name
        });
        emit("conditionsUpdated", conditions.value);
        conditionToEdit.value = undefined;
    })
}

function cancelEditConditionName() {
    conditionToEdit.value = undefined;
}

</script>
